import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { Icon } from "@/components/ui/Icon";
import { Avatar, AvatarStack, type AvatarUser } from "@/components/ui/Avatar";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { deriveAvatarColor, deriveInitials, resolveWorkspaceColor } from "@/lib/colors";
import { RauteMark } from "@/components/ui/RauteMark";
import { formatMemoDate, formatSeriesSchedule } from "@/lib/format";
import { BRAND } from "@/lib/tokens";
import { t } from "@/lib/strings";
import { useAuth } from "@/hooks/useAuth";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useMeetingSeries } from "@/hooks/useMeetingSeries";
import { NewSeriesDialog } from "@/components/dialogs/NewSeriesDialog";
import { ProtokolleHeader, primaryButtonStyle } from "@/components/memo/ui/ProtokolleHeader";
import { CountPill } from "@/components/memo/ui/CountPill";
import { workspacesUserCanCreateSeriesIn } from "@/lib/memoPermissions";
import type { MemoUser } from "@/lib/types";

// Mirror the person-picker mapping (UserMultiSelect / SeriesTasksList) so a
// MemoUser renders in the shared Avatar, which expects the derived AvatarUser.
function toAvatarUser(u: MemoUser): AvatarUser {
  const name = u.display_name ?? u.username ?? "?";
  return {
    id: u.id,
    display_name: name,
    initials: deriveInitials(name),
    avatar_color: deriveAvatarColor(u.id),
    username: u.username,
    department_workspace_id: u.department_workspace_id ?? null,
    department_color: u.department_color ?? null,
  };
}

// Bereich pill tint. The workspace colors are saturated brand hues, so a pure
// tint-as-text would be illegible in one theme or the other. Blending the hue
// with the theme's primary ink (`--ink-1`, which flips per theme) keeps the
// text legible on both grounds while staying recognisably the dept color; the
// translucent color-mix background mirrors the CountPill approach — no
// hardcoded pastel (flow_dark_mode_token_rule).
function bereichTint(color: string): { background: string; color: string } {
  return {
    background: `color-mix(in srgb, ${color} 16%, transparent)`,
    color: `color-mix(in srgb, ${color} 55%, var(--ink-1))`,
  };
}

export function MeetingsPage() {
  const { user } = useAuth();
  const { data: series = [], isLoading } = useMeetingSeries();
  const { data: workspaces = [] } = useQuery({
    queryKey: queryKeys.workspaces,
    queryFn: () => api.listWorkspaces(),
  });
  const isDesktop = useBreakpoint("md");
  const [newOpen, setNewOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [areaFilter, setAreaFilter] = useState<string>("all");

  const wsById = useMemo(() => new Map(workspaces.map((w) => [w.id, w])), [workspaces]);

  // The "Neue Besprechungsreihe" affordance only makes sense where the user is a
  // workspace owner/admin (or flow-admin) — the backend 403s otherwise.
  const canCreateSeries = workspacesUserCanCreateSeriesIn(workspaces, user).length > 0;

  // Filter options = the distinct Bereiche that actually have a series, in
  // stable name order, so the dropdown never lists an empty department.
  const areaOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const s of series) {
      if (seen.has(s.home_workspace_id)) continue;
      const ws = wsById.get(s.home_workspace_id);
      seen.set(s.home_workspace_id, ws?.name ?? s.home_workspace_id);
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "de"));
  }, [series, wsById]);

  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      series.filter((s) => {
        if (areaFilter !== "all" && s.home_workspace_id !== areaFilter) return false;
        if (!q) return true;
        const ws = wsById.get(s.home_workspace_id);
        return s.name.toLowerCase().includes(q) || (ws?.name ?? "").toLowerCase().includes(q);
      }),
    [series, areaFilter, q, wsById],
  );

  const hasSeries = series.length > 0;

  return (
    <div style={{ padding: isDesktop ? "28px 36px 80px" : "16px 12px 80px" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <Breadcrumb items={[{ label: t.navHome, to: "/" }, { label: t.meetingsTitle }]} />

        <div style={{ marginTop: 8 }}>
          <ProtokolleHeader
            kicker={{ color: BRAND.orange, label: t.meetingsKicker }}
            title={t.meetingsTitle}
            actions={
              canCreateSeries ? (
                <button type="button" onClick={() => setNewOpen(true)} style={primaryButtonStyle}>
                  <Icon name="plus" size={15} color="#fff" />
                  {t.meetingsNewSeries}
                </button>
              ) : null
            }
          />
        </div>

        {hasSeries ? (
          <div
            style={{
              marginTop: 18,
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flex: "1 1 240px",
                minWidth: 200,
                background: "var(--surface-soft)",
                border: "1px solid var(--hairline)",
                borderRadius: 8,
                padding: "8px 12px",
              }}
            >
              <Icon name="search" size={15} color="var(--ink-3)" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t.meetingsSearchPlaceholder}
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  color: "var(--ink-1)",
                  fontSize: 13,
                }}
              />
            </label>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "var(--surface-soft)",
                border: "1px solid var(--hairline)",
                borderRadius: 8,
                padding: "8px 12px",
              }}
            >
              <Icon name="filter" size={15} color="var(--ink-3)" />
              <select
                value={areaFilter}
                onChange={(e) => setAreaFilter(e.target.value)}
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  color: "var(--ink-1)",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                <option value="all">{t.meetingsAllAreas}</option>
                {areaOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : null}

        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          {!isLoading && !hasSeries ? (
            <div
              style={{
                color: "var(--ink-3)",
                fontSize: 14,
                padding: "40px 0",
                textAlign: "center",
              }}
            >
              {t.meetingsEmpty}
            </div>
          ) : null}
          {hasSeries && filtered.length === 0 ? (
            <div
              style={{
                color: "var(--ink-3)",
                fontSize: 14,
                padding: "40px 0",
                textAlign: "center",
              }}
            >
              {t.meetingsNoResults}
            </div>
          ) : null}
          {filtered.map((s) => {
            const ws = wsById.get(s.home_workspace_id);
            const color = resolveWorkspaceColor(ws, s.home_workspace_id);
            const tint = bereichTint(color);
            const cadence = formatSeriesSchedule(s.default_weekday, s.default_start_time);
            const lastPart = s.last_memo_date
              ? `${t.meetingsLastPrefix} ${formatMemoDate(s.last_memo_date)}`
              : "";
            const scheduleLine = [cadence, lastPart].filter(Boolean).join(" · ");
            const participants = s.participants.map(toAvatarUser);
            return (
              <Link
                key={s.id}
                to="/meetings/$seriesId"
                params={{ seriesId: s.id }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "14px 16px",
                  border: "1px solid var(--hairline)",
                  borderRadius: 10,
                  textDecoration: "none",
                  background: "var(--surface)",
                }}
              >
                <RauteMark color={color} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span
                      style={{
                        fontFamily: "var(--font-display)",
                        fontSize: 16,
                        fontWeight: 600,
                        letterSpacing: "-0.01em",
                        color: "var(--ink-1)",
                      }}
                    >
                      {s.name}
                    </span>
                    {ws ? (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          borderRadius: 999,
                          padding: "2px 9px",
                          fontSize: 11.5,
                          fontWeight: 600,
                          lineHeight: 1.3,
                          whiteSpace: "nowrap",
                          background: tint.background,
                          color: tint.color,
                        }}
                      >
                        {ws.name}
                      </span>
                    ) : null}
                  </div>
                  {scheduleLine ? (
                    <div style={{ color: "var(--ink-2)", fontSize: 13, marginTop: 3 }}>
                      {scheduleLine}
                    </div>
                  ) : null}
                </div>
                {participants.length > 0 ? (
                  participants.length === 1 ? (
                    <Avatar user={participants[0]} size={24} />
                  ) : (
                    <AvatarStack users={participants} size={24} max={3} />
                  )
                ) : null}
                <CountPill tone={s.open_task_count > 0 ? "open" : "done"}>
                  {s.open_task_count > 0
                    ? `${s.open_task_count} ${t.meetingsOpenSuffix}`
                    : t.meetingsAllDone}
                </CountPill>
                <Icon name="chevRight" size={16} color="var(--ink-4)" />
              </Link>
            );
          })}
        </div>
      </div>

      <NewSeriesDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}
